import type { EmbeddedPayload, PayloadType } from "./types";

export interface PayloadCandidate {
  path: string;
  bytes: Uint8Array;
  sha256: string;
}

const SCRIPT_EXTENSION =
  /\.(?:ps1|psm1|py|pyw|js|jse|vbs|vbe|sh|bash|zsh|bat|cmd)$/i;
const NATIVE_EXTENSION = /\.(?:exe|dll|scr|com|so|dylib|bin|dat)$/i;

function entropy(bytes: Uint8Array) {
  if (!bytes.length) return 0;
  const counts = new Uint32Array(256);
  for (const byte of bytes) counts[byte] += 1;
  let value = 0;
  for (const count of counts) {
    if (!count) continue;
    const probability = count / bytes.length;
    value -= probability * Math.log2(probability);
  }
  return Math.round(value * 100) / 100;
}

function asciiStrings(bytes: Uint8Array, limit = 1_500) {
  const strings: string[] = [];
  let current = "";
  const flush = () => {
    if (current.length >= 5) strings.push(current.slice(0, 4_096));
    current = "";
  };
  for (let index = 0; index < bytes.length && strings.length < limit; index += 1) {
    const byte = bytes[index];
    if (byte >= 0x20 && byte <= 0x7e) current += String.fromCharCode(byte);
    else flush();
  }
  flush();
  return strings;
}

function utf16Strings(bytes: Uint8Array, limit = 500) {
  const strings: string[] = [];
  let current = "";
  for (
    let index = 0;
    index + 1 < bytes.length && strings.length < limit;
    index += 2
  ) {
    const low = bytes[index];
    const high = bytes[index + 1];
    if (high === 0 && low >= 0x20 && low <= 0x7e) {
      current += String.fromCharCode(low);
    } else {
      if (current.length >= 5) strings.push(current.slice(0, 4_096));
      current = "";
    }
  }
  if (current.length >= 5) strings.push(current.slice(0, 4_096));
  return strings;
}

function u16(bytes: Uint8Array, offset: number, little = true) {
  if (offset + 2 > bytes.length) return 0;
  return new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength).getUint16(
    offset,
    little,
  );
}

function u32(bytes: Uint8Array, offset: number, little = true) {
  if (offset + 4 > bytes.length) return 0;
  return new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength).getUint32(
    offset,
    little,
  );
}

function peDetails(bytes: Uint8Array) {
  if (bytes[0] !== 0x4d || bytes[1] !== 0x5a) return null;
  const offset = u32(bytes, 0x3c);
  if (
    offset <= 0 ||
    offset + 24 > bytes.length ||
    bytes[offset] !== 0x50 ||
    bytes[offset + 1] !== 0x45
  )
    return null;
  const machine = u16(bytes, offset + 4);
  const sections = u16(bytes, offset + 6);
  const characteristics = u16(bytes, offset + 22);
  const architecture =
    {
      0x014c: "x86",
      0x8664: "x86-64",
      0xaa64: "ARM64",
      0x01c4: "ARM Thumb-2",
    }[machine] ?? `machine 0x${machine.toString(16)}`;
  return {
    type: "pe" as const,
    architecture,
    executable: Boolean(characteristics & 0x0002),
    details: [
      `PE/COFF ${architecture}`,
      `${sections} section${sections === 1 ? "" : "s"}`,
      characteristics & 0x2000 ? "DLL image" : "Executable image",
    ],
  };
}

function elfDetails(bytes: Uint8Array) {
  if (
    bytes[0] !== 0x7f ||
    bytes[1] !== 0x45 ||
    bytes[2] !== 0x4c ||
    bytes[3] !== 0x46
  )
    return null;
  const bits = bytes[4] === 2 ? "64-bit" : "32-bit";
  const little = bytes[5] !== 2;
  const machine = u16(bytes, 18, little);
  const architecture =
    {
      0x03: "x86",
      0x3e: "x86-64",
      0x28: "ARM",
      0xb7: "ARM64",
      0xf3: "RISC-V",
    }[machine] ?? `machine 0x${machine.toString(16)}`;
  return {
    type: "elf" as const,
    architecture,
    executable: true,
    details: [`ELF ${bits} ${architecture}`],
  };
}

function machODetails(bytes: Uint8Array) {
  const magic = u32(bytes, 0, false);
  if (magic === 0xcafebabe) {
    const architectureCount = u32(bytes, 4, false);
    if (architectureCount < 1 || architectureCount > 32) return null;
  }
  const known = new Map([
    [0xfeedface, "Mach-O 32-bit"],
    [0xfeedfacf, "Mach-O 64-bit"],
    [0xcafebabe, "Mach-O universal"],
  ]);
  const label = known.get(magic);
  return label
    ? {
        type: "mach-o" as const,
        architecture: label.replace("Mach-O ", ""),
        executable: true,
        details: [label],
      }
    : null;
}

function scriptType(path: string, text: string): PayloadType | null {
  if (/\.ps(?:1|m1)$/i.test(path) || /\b(?:powershell|pwsh)\b/i.test(text))
    return "powershell";
  if (/\.pyw?$/i.test(path) || /^#!.*python/im.test(text)) return "python";
  if (/\.(?:js|jse)$/i.test(path) || /^#!.*node/im.test(text))
    return "javascript";
  if (/\.(?:bat|cmd)$/i.test(path) || /@echo\s+off/i.test(text)) return "batch";
  if (/\.(?:sh|bash|zsh)$/i.test(path) || /^#!.*(?:ba|z|k)?sh/im.test(text))
    return "shell";
  return null;
}

const CAPABILITIES: Array<[string, RegExp]> = [
  [
    "process execution",
    /\b(?:CreateProcess[AW]?|ShellExecute[AW]?|WinExec|system\(|popen\(|subprocess\.|child_process|Runtime\.exec|ProcessBuilder)\b/i,
  ],
  [
    "outbound networking",
    /\b(?:WinHttp\w*|WinInet\w*|InternetOpen\w*|URLDownloadToFile[AW]?|socket\(|requests\.(?:get|post)|urllib|curl\b|wget\b|Invoke-WebRequest|DownloadString)\b/i,
  ],
  [
    "credential or token access",
    /\b(?:Login Data|Local State|launcher_accounts|access.?token|refresh.?token|CryptUnprotectData|key4\.db|logins\.json|wallet\.dat)\b/i,
  ],
  [
    "security-product tampering",
    /\b(?:Add-MpPreference|Set-MpPreference|WinDefend|DisableRealtimeMonitoring|advfirewall|amsiutils|AmsiScanBuffer)\b/i,
  ],
  [
    "persistence",
    /\b(?:CurrentVersion\\Run|schtasks|Startup|LaunchAgents|crontab|systemctl\s+enable)\b/i,
  ],
  [
    "screen or input capture",
    /\b(?:BitBlt|GetAsyncKeyState|SetWindowsHookEx|keylog|screenshot|ImageGrab|pyautogui|clipboard)\b/i,
  ],
  [
    "archive extraction",
    /\b(?:Expand-Archive|ZipFile|tarfile|shutil\.unpack_archive|7z(?:\.exe)?|unzip)\b/i,
  ],
  [
    "dynamic code loading",
    /\b(?:LoadLibrary[AW]?|GetProcAddress|VirtualAlloc|WriteProcessMemory|CreateRemoteThread|exec\(|eval\(|Assembly\.Load)\b/i,
  ],
  [
    "encoded command",
    /(?:-(?:enc|encodedcommand)\s+[A-Za-z0-9+/=]{20,}|frombase64string|base64\.b64decode)/i,
  ],
];

function indicators(strings: string[]) {
  const found = new Set<string>();
  for (const value of strings) {
    for (const match of value.matchAll(
      /https?:\/\/[^\s"'<>\\)\]}]{4,}|(?:\b(?:\d{1,3}\.){3}\d{1,3}\b)/gi,
    )) {
      found.add(match[0].slice(0, 500));
      if (found.size >= 80) break;
    }
    if (found.size >= 80) break;
  }
  return [...found];
}

function riskFor(
  executable: boolean,
  packed: boolean,
  capabilities: string[],
  foundIndicators: string[],
): EmbeddedPayload["risk"] {
  const has = (value: string) => capabilities.includes(value);
  if (
    has("credential or token access") &&
    has("outbound networking") &&
    (has("process execution") || has("dynamic code loading"))
  )
    return "critical";
  if (
    (has("security-product tampering") || has("persistence")) &&
    (has("process execution") || executable)
  )
    return "critical";
  if (
    capabilities.length >= 3 ||
    (executable && foundIndicators.length > 0) ||
    (packed && executable)
  )
    return "high";
  if (capabilities.length || executable) return "medium";
  return "context";
}

export function inspectEmbeddedPayloads(
  candidates: PayloadCandidate[],
): EmbeddedPayload[] {
  const payloads: EmbeddedPayload[] = [];
  for (const candidate of candidates) {
    if (payloads.length >= 100) break;
    const { path, bytes, sha256 } = candidate;
    const pe = peDetails(bytes);
    const elf = elfDetails(bytes);
    const macho = machODetails(bytes);
    const textSample = new TextDecoder("utf-8", { fatal: false }).decode(
      bytes.subarray(0, Math.min(bytes.length, 2 * 1024 * 1024)),
    );
    const scripted = SCRIPT_EXTENSION.test(path)
      ? scriptType(path, textSample)
      : null;
    const native = pe ?? elf ?? macho;
    if (!native && !scripted && !NATIVE_EXTENSION.test(path)) continue;

    const strings = unique([
      ...asciiStrings(bytes),
      ...utf16Strings(bytes),
      ...textSample.split(/\r?\n/).filter((line) => line.trim().length >= 5),
    ]).slice(0, 2_500);
    const capabilities = CAPABILITIES.filter(([, pattern]) =>
      strings.some((value) => {
        pattern.lastIndex = 0;
        return pattern.test(value);
      }),
    ).map(([label]) => label);
    const foundIndicators = indicators(strings);
    const score = entropy(bytes.subarray(0, Math.min(bytes.length, 8 * 1024 * 1024)));
    const packed =
      score >= 7.45 ||
      strings.some((value) => /\b(?:UPX0|UPX1|MPRESS|Themida|VMProtect)\b/i.test(value));
    const type =
      native?.type ??
      scripted ??
      (/\.so$|\.dylib$/i.test(path)
        ? "native-library"
        : "opaque-binary");
    const executable = native?.executable ?? Boolean(scripted);
    const suspiciousStrings = strings
      .filter((value) =>
        CAPABILITIES.some(([, pattern]) => {
          pattern.lastIndex = 0;
          return pattern.test(value);
        }),
      )
      .slice(0, 50);
    payloads.push({
      id: `payload-${payloads.length + 1}`,
      path,
      type,
      architecture: native?.architecture,
      size: bytes.length,
      sha256,
      entropy: score,
      executable,
      packed,
      risk: riskFor(executable, packed, capabilities, foundIndicators),
      capabilities,
      indicators: foundIndicators,
      suspiciousStrings,
      details: [
        ...(native?.details ?? []),
        scripted ? `${scripted} script` : "",
        packed ? "High entropy or packer marker" : "",
        `${strings.length.toLocaleString()} printable strings inspected`,
      ].filter(Boolean),
    });
  }
  return payloads.sort((left, right) => {
    const order = { critical: 0, high: 1, medium: 2, context: 3 };
    return order[left.risk] - order[right.risk];
  });
}

function unique(values: string[]) {
  return Array.from(new Set(values));
}
